// API Client for Sunlight Grounds Care Client Portal
// Routes through our proxy backend which forwards to the real backend
// Falls back to mock data if backend is unreachable

import axios from 'axios';
import { mockApi } from './mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance pointing to OUR proxy backend
const axiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 12000,
});

// Add auth token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Guard to prevent multiple 401 redirects
let isRedirecting = false;

// Handle 401 responses — but NOT during login or if already redirecting
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/login');
      if (!isLoginRequest && !isRedirecting) {
        isRedirecting = true;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedProperty');
        localStorage.removeItem('api_mode');
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Real API functions — corrected endpoint paths per backend team
const realApi = {
  login: async (email, password) => {
    const response = await axiosInstance.post('/login', { email, password });
    return response.data;
  },

  getProperties: async () => {
    const response = await axiosInstance.get('/client/properties');
    // Backend returns { properties: [...] }, extract the array
    return response.data.properties || response.data;
  },

  getDashboardData: async (propertyId, date) => {
    const response = await axiosInstance.get('/client/dashboard-data', {
      params: { property_id: propertyId, date }
    });
    return response.data;
  },

  getLandscapeSchedule: async (propertyId) => {
    const response = await axiosInstance.get('/client/landscape/schedule', {
      params: propertyId ? { property_id: propertyId } : undefined
    });
    // Backend returns { schedule: [...], properties: [...] }
    const data = response.data;
    return { schedule: data.schedule || [], properties: data.properties || [] };
  },

  getStaffStatus: async (propertyId) => {
    const response = await axiosInstance.get('/client/staff-status', {
      params: { property_id: propertyId }
    });
    // Backend returns { staff_status: [...], total_staff: N }
    return response.data.staff_status || response.data;
  },

  // Corrected: /property-staff does not exist, use /staff-status
  getPropertyStaff: async (propertyId) => {
    const response = await axiosInstance.get('/client/property-staff', {
      params: { property_id: propertyId }
    });
    return response.data;
  },

  // Corrected: /services does not exist, use /service-request-types
  getServices: async (propertyId) => {
    const response = await axiosInstance.get('/client/service-request-types', {
      params: { property_id: propertyId }
    });
    return response.data.types || response.data;
  },

  getRecommendations: async (propertyId) => {
    const response = await axiosInstance.get(`/client/properties/${propertyId}/recommendations`);
    return response.data;
  },

  getLandscapePhotos: async (propertyId) => {
    const response = await axiosInstance.get('/client/landscape/photos', {
      params: propertyId ? { property_id: propertyId } : undefined
    });
    // Backend returns { photos: [...], spaces: [...] }
    return response.data;
  },

  getServiceRequestTypes: async (propertyId) => {
    const response = await axiosInstance.get('/client/service-request-types', {
      params: { property_id: propertyId }
    });
    return response.data.types || response.data;
  },

  getLocations: async (propertyId) => {
    const response = await axiosInstance.get('/client/locations', {
      params: { property_id: propertyId }
    });
    return response.data.locations || response.data;
  },

  getServiceRequests: async (propertyId) => {
    const response = await axiosInstance.get('/client/service-requests', {
      params: { property_id: propertyId }
    });
    return response.data.requests || response.data;
  },

  createServiceRequest: async (data) => {
    const response = await axiosInstance.post('/client/service-requests', data);
    return response.data;
  },

  // Corrected: /properties/{id}/info does not exist, use /client/dashboard
  getPropertyInfo: async (propertyId) => {
    const response = await axiosInstance.get('/client/dashboard', {
      params: { property_id: propertyId }
    });
    return response.data;
  },

  getBilling: async () => {
    const response = await axiosInstance.get('/client/invoices');
    // Backend returns { invoices: [...], ytd_total, total_due, count }
    return response.data;
  },

  getInvoices: async (propertyId) => {
    const response = await axiosInstance.get('/client/invoices', {
      params: propertyId ? { property_id: propertyId } : undefined
    });
    return response.data;
  },

  // Corrected: /checkout-session → /pay
  payInvoice: async (invoiceId) => {
    const response = await axiosInstance.post(`/client/invoices/${invoiceId}/pay`);
    return response.data;
  },

  getInvoicePdf: async (invoiceId) => {
    const response = await axiosInstance.get(`/client/invoices/${invoiceId}/pdf`);
    return response.data;
  },

  createSetupIntent: async () => {
    const response = await axiosInstance.post('/client/create-setup-intent');
    return response.data;
  },

  getBillingPortal: async () => {
    const response = await axiosInstance.post('/client/billing-portal');
    return response.data;
  },

  getWeather: async () => {
    // Open-Meteo free API — no key needed. Hardcoded to Arcadia/Pasadena, CA area.
    // Calculate Monday–Saturday of the current week
    const today = new Date();
    const dow = today.getDay(); // 0=Sun,1=Mon,...,6=Sat
    const monOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today);
    monday.setDate(today.getDate() + monOffset);
    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);
    const fmt = (d) => d.toISOString().split('T')[0];

    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=34.13&longitude=-118.04&current=temperature_2m,weather_code,relative_humidity_2m,wind_speed_10m&daily=weather_code,temperature_2m_max&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America/Los_Angeles&start_date=${fmt(monday)}&end_date=${fmt(saturday)}`
    );
    const data = await res.json();
    const wmoToCondition = (code) => {
      if (code <= 1) return 'Clear';
      if (code <= 3) return 'Partly cloudy';
      if (code <= 48) return 'Foggy';
      if (code <= 57) return 'Drizzle';
      if (code <= 67) return 'Rain';
      if (code <= 77) return 'Snow';
      if (code <= 82) return 'Showers';
      if (code <= 86) return 'Snow showers';
      return 'Thunderstorm';
    };
    const wmoToIcon = (code) => {
      if (code <= 1) return '☀️';
      if (code <= 3) return '⛅';
      if (code <= 48) return '🌫️';
      if (code <= 67) return '🌧️';
      if (code <= 77) return '❄️';
      if (code <= 86) return '🌨️';
      return '⛈️';
    };
    const dayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    return {
      temp: Math.round(data.current.temperature_2m),
      condition: wmoToCondition(data.current.weather_code),
      humidity: data.current.relative_humidity_2m,
      wind: Math.round(data.current.wind_speed_10m),
      icon: wmoToIcon(data.current.weather_code),
      forecast: data.daily.time.map((t, i) => ({
        day: dayLabels[i],
        date: new Date(t + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        temp: Math.round(data.daily.temperature_2m_max[i]),
        icon: wmoToIcon(data.daily.weather_code[i])
      }))
    };
  },

  // Grounds Care Routes — returns scheduled crew visits
  getGroundsCareRoutes: async () => {
    const response = await axiosInstance.get('/grounds-care/routes');
    return response.data.routes || [];
  },

  // Property Info — access info, SOPs, property notes (Client Visible)
  getPropertyInfo: async (propertyId) => {
    const response = await axiosInstance.get('/client/landscape/property-info', {
      params: propertyId ? { property_id: propertyId } : undefined
    });
    const props = response.data.properties || [];
    return props.find(p => p.id === propertyId) || props[0] || null;
  },

  getMessages: async () => {
    return [];
  },

  sendMessage: async (text) => {
    throw new Error('Message API not implemented');
  }
};

// Smart API: tries real backend, falls back to mock on errors
const smartApi = {
  login: async (email, password) => {
    try {
      const data = await realApi.login(email, password);
      localStorage.setItem('api_mode', 'live');
      return data;
    } catch (err) {
      // Real error from backend (wrong password, pending account) — don't fallback
      if (err.response?.status === 401 || err.response?.status === 403) {
        throw err;
      }
      // Server down or proxy error — fall back to mock
      console.warn('Backend unreachable, using demo mode');
      localStorage.setItem('api_mode', 'mock');
      return mockApi.login(email, password);
    }
  },
};

// Proxy handler: routes to real or mock based on login result
const handler = {
  get(target, prop) {
    if (prop === 'login') return smartApi.login;

    return (...args) => {
      const mode = localStorage.getItem('api_mode');
      if (mode === 'live' && realApi[prop]) {
        return realApi[prop](...args);
      }
      if (mockApi[prop]) return mockApi[prop](...args);
      if (realApi[prop]) return realApi[prop](...args);
      throw new Error(`API method ${prop} not found`);
    };
  }
};

const api = new Proxy({}, handler);

export default api;
export { BACKEND_URL };
